import React, { ReactNode, useCallback, useEffect } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Paper,
  TextField,
  Grid,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Switch,
  FormHelperText,
  Stack,
  IconButton,
  Tooltip,
  InputAdornment,
  FormLabel,
} from "@mui/material";
import { Box } from "@mui/system";

import UploadImage from "@app/components/molecules/UploadImage";
import UploadAudio from "@app/components/molecules/UploadAudio";
import { MEDIA_PAGE_TYPE } from "@app/constants/enums";
import SelectUseForm from "@app/components/organisms/SelectUseForm";
import {
  BOOLEAN_OPTIONS,
  MEDIA_PAGE_TYPE_OPTIONS,
} from "@app/constants/optionsSelect";
import { regex } from "@app/constants";
import { FiHelpCircle } from "react-icons/fi";

export type TLessonForm = {
  image?: string;
  name: string;
  description?: string;
  mediaType?: MEDIA_PAGE_TYPE;
  mediaUrl?: string;
  publish: boolean;
  minScore: boolean;
  minScoreValue: number;
  canRetake: boolean;
  showResult: boolean;
  randomQuestions: boolean;
  randomAnswers: boolean;
};

type LessonFormProps = {
  onValidateSuccess: (data: TLessonForm) => void;
  footerActions: ReactNode;
  dataForm?: TLessonForm;
  edit?: boolean;
};

const LessonForm = ({
  onValidateSuccess,
  footerActions,
  dataForm,
  edit,
}: LessonFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        image: yup.string(),
        name: yup.string().required("Preencha o campo nome"),
        description: yup.string(),
        mediaType: yup
          .mixed()
          .oneOf([
            MEDIA_PAGE_TYPE.NONE,
            MEDIA_PAGE_TYPE.AUDIO,
            MEDIA_PAGE_TYPE.EMBED,
          ]),
        mediaUrl: yup.string(),
        publish: yup
          .boolean()
          .required("Escolha se essa aula será publicada ou não"),
        minScore: yup.boolean(),
        minScoreValue: yup
          .number()
          .optional()
          .when("minScore", {
            is: true,
            then: yup
              .number()
              .required("Coloque uma porcetagem de acerto para nota mínima.")
              .max(100),
            otherwise: yup.number().nullable(),
          }),
        canRetake: yup.boolean(),
        showResult: yup.boolean(),
        randomQuestions: yup.boolean(),
        randomAnswers: yup.boolean(),
      })
    ),
    defaultValues: {
      image: "",
      name: "",
      description: "",
      mediaType: MEDIA_PAGE_TYPE.NONE,
      mediaUrl: "",
      publish: true,
      minScore: false,
      minScoreValue: 0,
      canRetake: true,
      showResult: false,
      randomQuestions: false,
      randomAnswers: false,
    },
    mode: "onSubmit",
  });

  const handleSetImageLesson = useCallback(
    (imageUrl?: string) => {
      if (imageUrl) {
        setValue("image", imageUrl);
      } else {
        setValue("image", "");
      }
    },
    [setValue]
  );

  const handleGetTYoutubeId = () => {
    const { mediaUrl } = getValues();

    var match = mediaUrl.match(regex.youtubeUrl);
    if (match && match[2].length === 11) {
      return match[2];
    }
  };

  const onSubmit = (data: TLessonForm) => {
    onValidateSuccess(data);
  };

  const handleSetMediaUrl = useCallback(
    (fileUrl?: string) => {
      if (fileUrl) {
        setValue("mediaUrl", fileUrl);
      } else {
        setValue("mediaUrl", "");
      }
    },
    [setValue]
  );

  const [
    mediaType,
    mediaUrl,
    minScore,
    canRetake,
    showResult,
    randomQuestions,
    randomAnswers,
    image,
  ] = watch([
    "mediaType",
    "mediaUrl",
    "minScore",
    "canRetake",
    "showResult",
    "randomQuestions",
    "randomAnswers",
    "image",
  ]);

  useEffect(() => {
    setValue("canRetake", minScore);
    setValue("minScoreValue", minScore ? 60 : 0);
  }, [minScore, setValue]);

  useEffect(() => {
    reset(dataForm);
  }, [dataForm, reset]);

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid
            container
            xs={12}
            sm={12}
            sx={{ marginTop: 5 }}
            justifyContent="center"
            alignItems="center"
          >
            <UploadImage
              label="Escolha uma capa para a aula"
              destination="LESSON"
              imageUrl={image}
              onRemoveImage={handleSetImageLesson}
              onUploadSuccess={handleSetImageLesson}
              error={!!errors.image}
              errorMessage={errors.image?.message}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              id="name"
              type="text"
              fullWidth
              margin="normal"
              variant="outlined"
              label="Nome"
              InputLabelProps={{ shrink: true }}
              error={!!errors.name}
              helperText={!!errors.name && errors.name.message}
              {...register("name")}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              id="description"
              label="Descrição"
              margin="normal"
              fullWidth
              InputProps={{
                rows: 5,
                multiline: true,
                inputComponent: "textarea",
              }}
              rows={5}
              multiline
              InputLabelProps={{ shrink: true }}
              error={!!errors.description}
              helperText={
                (!!errors.description && errors.description.message) ||
                "Para marcar textos para que eles possam ser escutados pelos seus alunos use <<>>, exemplo: My <<car>> is red, com essa marcação seu a palavra car será pronunciada no idioma da turma."
              }
              {...register("description")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectUseForm
              error={!!errors.mediaType}
              helperText={errors.mediaType?.message}
              control={control}
              label="Tipo da media"
              defaultValue={String(getValues("mediaType"))}
              {...register("mediaType")}
            >
              {MEDIA_PAGE_TYPE_OPTIONS.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </SelectUseForm>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectUseForm
              error={!!errors.publish}
              helperText={errors.publish?.message}
              control={control}
              label="Publicar aula?"
              defaultValue={String(getValues("publish"))}
              {...register("publish")}
            >
              {BOOLEAN_OPTIONS.map((option, index, _) => (
                <MenuItem key={index} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </SelectUseForm>
          </Grid>

          {mediaType === MEDIA_PAGE_TYPE.EMBED && (
            <Grid item xs={12} sm={12}>
              <TextField
                id="mediaUrl"
                label={"Url"}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.mediaUrl}
                helperText={!!errors.mediaUrl && errors.mediaUrl.message}
                {...register("mediaUrl")}
              />
            </Grid>
          )}
          {mediaType === MEDIA_PAGE_TYPE.AUDIO && (
            <Grid item xs={12} sm={6}>
              <UploadAudio
                url={getValues("mediaUrl")}
                error={!!errors.mediaUrl}
                errorMessage={errors.mediaUrl?.message}
                onRemoveFile={handleSetMediaUrl}
                onUploadSuccess={handleSetMediaUrl}
              />
            </Grid>
          )}

          {mediaUrl && mediaType === MEDIA_PAGE_TYPE.EMBED && (
            <Grid item xs={12} sm={6}>
              <iframe
                width="450"
                height="255"
                src={`https://www.youtube.com/embed/${handleGetTYoutubeId()}`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ borderRadius: 5, borderWidth: 0, marginTop: 20 }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12}>
            <FormLabel component="legend">Configurações da aula</FormLabel>
            <FormGroup>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getValues("minScore")}
                      {...register("minScore")}
                    />
                  }
                  label="Exigir nota mínima?"
                />
                {minScore && (
                  <TextField
                    id="standard-basic"
                    label="Percentual de acerto"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    type="number"
                    error={!!errors.minScoreValue}
                    helperText={
                      !!errors.minScoreValue && errors.minScoreValue.message
                    }
                    {...register("minScoreValue")}
                    placeholder="Ex: 60%"
                  />
                )}
                <Tooltip title="Ao exigir uma nota mínima, a opção de 'Essa aula pode ser refeita?' será marcada obrigatoriamente, para que que o aluno sempre tenha oportunidade concluir a aula.">
                  <IconButton color="primary">
                    <FiHelpCircle />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      {...register("canRetake")}
                      checked={canRetake}
                      disabled={minScore}
                    />
                  }
                  label="Essa aula pode ser refeita?"
                />
              </Stack>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch {...register("showResult")} checked={showResult} />
                  }
                  label="Exibir respostas após resultado?"
                />
              </Stack>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      {...register("randomQuestions")}
                      checked={randomQuestions}
                    />
                  }
                  label="Colocar perguntas em ordem aleátoria?"
                />
              </Stack>
              <Stack direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      {...register("randomAnswers")}
                      checked={randomAnswers}
                    />
                  }
                  label="Colocar respostas em ordem aleátoria? "
                />
              </Stack>
              <FormHelperText>
                Válido apenas para perguntas do tipo multipla escolha
              </FormHelperText>
            </FormGroup>
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ mt: 10 }}>{footerActions}</Box>
    </form>
  );
};

export default LessonForm;
